import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  AccessTokenProvider,
  ApiProvider,
  HeyApiProvider,
  defaultQueryClientConfig,
} from "@platform/api";
import { logger } from "@platform/browser-logger";
import { NotesDrawer, SendRfpDrawer } from "@platform/coverage-overview";
import { DialogServiceProvider } from "@platform/design-system";
import { RecordInspectorDrawer } from "@platform/entity-explorer";
import { NotificationProvider } from "@platform/hooks";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useContext } from "react";

import BenefitsClassManagerDrawer from "./components/BenefitsClassManagerDrawer";
import CloseDialogsOnLogout from "./components/CloseDialogsOnLogout";
import UserFeatureFlagsProvider from "./components/UserFeatureFlagsProvider";
import RFPNoteDrawer from "./components/RFPNoteDrawer";
import SessionProvider from "./components/SessionProvider";
import {
  AnalyticsProvider,
  AuthContext,
  AuthProvider,
  ToastNotificationServiceProvider,
} from "./contexts";
import { ScenarioNotesDrawer } from "./pages/BBE/components/ScenarioNotesDrawer/ScenarioNotesDrawer";
import GenerateExportMpDrawer from "./pages/RFPDetails/components/Present/GenerateExportMpDrawer";
import { NeedsReviewCommentsDrawer } from "./pages/RFPDetails/components/Present/NeedsReviewCommentsDrawer";
import { ThemeSelector } from "./theme/theme-selector";

logger.info("Broker portal initialized!");

// When running against the local mock server (VITE_ENABLE_PROXY=true), seed
// localStorage with a mock auth state so the app boots as authenticated without
// going through the real PKCE flow. The token matches MOCK_ACCESS_TOKEN in
// src/apps/mock-server/index.mjs — update both if the payload changes.
//
// This is NOT a secret. The signature segment decodes to the literal string
// "mock-signature-not-verified" and is only accepted by the local mock server —
// never by Auth0 or any real backend service.
if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_PROXY === "true") {
  if (!localStorage.getItem("authState")) {
    // Must stay in sync with MOCK_ACCESS_TOKEN in src/apps/mock-server/index.mjs
    const MOCK_TOKEN =
      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9" +
      ".eyJzdWIiOiJtb2NrLXVzZXItMDAxIiwibmFtZSI6Ik1vY2sgRGV2IFVzZXIiLCJnaXZlbl9uYW1lIjoiTW9jayIsImZhbWlseV9uYW1lIjoiVXNlciIsImVtYWlsIjoiZGV2QGNlbnRyb2JlbmVmaXRzLmNvbSIsImVtYWlscyI6WyJkZXZAY2VudHJvYmVuZWZpdHMuY29tIl0sImNlbnRyb19icC9yb2xlcyI6WyJhbmFseXN0Il0sImV4dGVuc2lvbl9CUF9VU0VSX1RZUEUiOiJhbmFseXN0IiwiZXhwIjoxODExOTY1MzIzLCJpYXQiOjE3ODA0MjkzMjN9" +
      ".bW9jay1zaWduYXR1cmUtbm90LXZlcmlmaWVk";
    localStorage.setItem(
      "authState",
      JSON.stringify({
        access_token: MOCK_TOKEN,
        id_token: MOCK_TOKEN,
        refresh_token: "mock-refresh-token",
      }),
    );
  }
}

const queryClient = new QueryClient(defaultQueryClientConfig);

const dialogRegistry = {
  benefitsClassManager: BenefitsClassManagerDrawer,
  rfpNotes: RFPNoteDrawer,
  sendRfp: SendRfpDrawer,
  notes: NotesDrawer,
  bbeScenarioNotes: ScenarioNotesDrawer,
  generateExportMp: GenerateExportMpDrawer,
  needsReviewComments: NeedsReviewCommentsDrawer,
  recordInspector: RecordInspectorDrawer,
};

// Inner provider component that accesses AuthContext to provide axios to ApiProvider
const ApiProviderWrapper = ({ children }) => {
  const { axiosInstance, token } = useContext(AuthContext);

  return (
    <ApiProvider axiosInstance={axiosInstance}>
      <AccessTokenProvider token={token}>
        <HeyApiProvider axiosInstance={axiosInstance}>
          {children}
        </HeyApiProvider>
      </AccessTokenProvider>
    </ApiProvider>
  );
};

const DialogServiceProviderWrapper = ({ children }) => {
  const { isAuthenticated } = useContext(AuthContext);

  return (
    <DialogServiceProvider
      dialogComponents={dialogRegistry}
      config={{
        syncWithUrl: isAuthenticated, // Only sync with URL if the user is authenticated to avoid issues on login page
        defaultWidth: "90vw",
      }}
    >
      <CloseDialogsOnLogout isAuthenticated={isAuthenticated} />
      {children}
    </DialogServiceProvider>
  );
};

const Providers = ({ children }) => (
  <QueryClientProvider client={queryClient}>
    <SessionProvider>
      <AuthProvider>
        <ApiProviderWrapper>
          <UserFeatureFlagsProvider>
            <ThemeSelector>
              <NotificationProvider>
                <ToastNotificationServiceProvider>
                  <AnalyticsProvider>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DialogServiceProviderWrapper>
                        {children}
                      </DialogServiceProviderWrapper>
                    </LocalizationProvider>
                  </AnalyticsProvider>
                </ToastNotificationServiceProvider>
              </NotificationProvider>
            </ThemeSelector>
          </UserFeatureFlagsProvider>
        </ApiProviderWrapper>
      </AuthProvider>
    </SessionProvider>
  </QueryClientProvider>
);

export default Providers;
